import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { FeedbackWidget } from '@/components/feedback/FeedbackWidget';
import { ExecutionLaunchInputs } from '@/components/execution/ExecutionLaunchInputs';
import { extractLaunchInputs } from '@/lib/execution-launch-inputs';
import { ExecutionStatusBadge } from '@/components/execution/ExecutionStatusBadge';
import { ExecutionTimelineRail } from '@/components/execution/ExecutionTimelineRail';
import { ExecutionChatPanel } from '@/components/execution-chat';
import { WorkActionsProvider } from '@/components/work/WorkActionsContext';
import { ViewRouter } from '@/components/views/ViewRouter';
import type { FlowExecution, ExecutionStep } from '@shared/types/flow-executions';

const REALTIME_DEBUG_ENABLED = import.meta.env.VITE_DEBUG_EXECUTION_REALTIME === 'true';
const FALLBACK_POLL_INTERVAL_MS = 2000;
const RUNNING_EXECUTION_STATUSES = new Set<FlowExecution['status']>([
  'pending',
  'running',
  'paused',
]);

const debugRealtime = (
  executionId: string | undefined,
  message: string,
  data?: Record<string, unknown>
): void => {
  if (!REALTIME_DEBUG_ENABLED) return;
  if (data) {
    console.info(`[execution-realtime:${executionId ?? 'unknown'}] ${message}`, data);
    return;
  }
  console.info(`[execution-realtime:${executionId ?? 'unknown'}] ${message}`);
};

const mergeExecutionState = (
  previous: FlowExecution | null,
  incoming: FlowExecution
): FlowExecution => (previous ? { ...previous, ...incoming } : incoming);

const mergeExecutionSteps = (
  previous: ExecutionStep[],
  incoming: ExecutionStep[]
): ExecutionStep[] => {
  const byId = new Map<string, ExecutionStep>();

  for (const step of previous) byId.set(step.id, step);
  for (const step of incoming) byId.set(step.id, step);

  return Array.from(byId.values()).sort((a, b) => a.position - b.position);
};

const extractExecutionErrorMessage = (error: FlowExecution['error']): string | null => {
  if (!error || typeof error !== 'object') return null;
  const message =
    (error as Record<string, unknown>).message ??
    (error as Record<string, unknown>).errorMessage ??
    (error as Record<string, unknown>).error;
  return typeof message === 'string' && message.trim().length > 0 ? message.trim() : null;
};

export const ExecutionDetailPage = () => {
  const { id } = useParams();
  const [execution, setExecution] = useState<FlowExecution | null>(null);
  const [steps, setSteps] = useState<ExecutionStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRealtimeDegraded, setIsRealtimeDegraded] = useState(false);
  const [isTimelineDrawerOpen, setIsTimelineDrawerOpen] = useState(false);
  const [documentTitle, setDocumentTitle] = useState<string | null>(null);
  const [focusedStepTarget, setFocusedStepTarget] = useState<{
    stepId: string;
    requestId: number;
  } | null>(null);
  const fetchRequestIdRef = useRef(0);

  const fetchExecutionAndSteps = useCallback(
    async (reason: string) => {
      if (!id) return;

      const requestId = ++fetchRequestIdRef.current;
      debugRealtime(id, 'fetch:start', { reason, requestId });

      try {
        const [execRes, stepsRes] = await Promise.all([
          supabase.from('flow_executions').select('*').eq('id', id).single(),
          supabase
            .from('execution_steps')
            .select('*')
            .eq('execution_id', id)
            .order('position'),
        ]);

        if (requestId !== fetchRequestIdRef.current) {
          debugRealtime(id, 'fetch:stale_response_ignored', { reason, requestId });
          return;
        }

        if (execRes.error) {
          console.error('Failed to fetch execution:', execRes.error);
        }

        if (stepsRes.error) {
          console.error('Failed to fetch execution steps:', stepsRes.error);
        }

        if (execRes.data) {
          setExecution((previous) =>
            mergeExecutionState(previous, execRes.data as FlowExecution)
          );
        }

        if (stepsRes.data) {
          setSteps((previous) =>
            mergeExecutionSteps(previous, stepsRes.data as ExecutionStep[])
          );
        }

        setLoading(false);
        debugRealtime(id, 'fetch:done', {
          reason,
          requestId,
          executionStatus: execRes.data?.status ?? null,
          stepCount: stepsRes.data?.length ?? 0,
        });
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) return;
        setLoading(false);
        console.error('Failed to fetch execution detail data:', error);
      }
    },
    [id]
  );

  useEffect(() => {
    if (!id) return;

    let isActive = true;
    setExecution(null);
    setSteps([]);
    setLoading(true);
    setIsRealtimeDegraded(false);
    setIsTimelineDrawerOpen(false);
    setFocusedStepTarget(null);
    setDocumentTitle(null);
    void fetchExecutionAndSteps('initial_load');

    const channel = supabase
      .channel(`execution-${id}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'execution_steps',
          filter: `execution_id=eq.${id}`,
        },
        (payload) => {
          if (!isActive || payload.eventType === 'DELETE') return;

          const updated = payload.new as ExecutionStep | null;
          if (!updated || typeof updated.id !== 'string') return;

          setSteps((previous) => mergeExecutionSteps(previous, [updated]));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'flow_executions',
          filter: `id=eq.${id}`,
        },
        (payload) => {
          if (!isActive) return;

          const updated = payload.new as FlowExecution | null;
          if (!updated || typeof updated.id !== 'string') return;

          setExecution((previous) => mergeExecutionState(previous, updated));
        }
      )
      .subscribe((status) => {
        if (!isActive) return;

        debugRealtime(id, 'channel:status', { status });
        switch (status) {
          case 'SUBSCRIBED': {
            setIsRealtimeDegraded(false);
            void fetchExecutionAndSteps('realtime_subscribed_catchup');
            break;
          }
          case 'CHANNEL_ERROR':
          case 'TIMED_OUT':
          case 'CLOSED': {
            setIsRealtimeDegraded(true);
            break;
          }
          default:
            break;
        }
      });

    return () => {
      isActive = false;
      fetchRequestIdRef.current += 1;
      void supabase.removeChannel(channel);
    };
  }, [id, fetchExecutionAndSteps]);

  const executionStatus = execution?.status;

  const handleTimelineStepSelect = useCallback((step: ExecutionStep) => {
    setFocusedStepTarget((current) => ({
      stepId: step.id,
      requestId: (current?.requestId ?? 0) + 1,
    }));
    setIsTimelineDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (!id || !executionStatus) return;
    if (!RUNNING_EXECUTION_STATUSES.has(executionStatus)) return;

    debugRealtime(id, 'active_polling:start', {
      status: executionStatus,
      intervalMs: FALLBACK_POLL_INTERVAL_MS,
      realtimeDegraded: isRealtimeDegraded,
    });

    const intervalId = window.setInterval(() => {
      void fetchExecutionAndSteps(
        isRealtimeDegraded ? 'polling_fallback' : 'active_status_catchup'
      );
    }, FALLBACK_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      debugRealtime(id, 'active_polling:stop', { status: executionStatus });
    };
  }, [id, executionStatus, fetchExecutionAndSteps, isRealtimeDegraded]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Exécution introuvable.</p>
      </div>
    );
  }

  const snapshot = execution.flow_snapshot as {
    title?: string;
    dominos?: Array<{ position?: number; action_type?: string; label?: string }>;
  } | null;
  const toolResultHref = execution.flow_id
    ? `/tools/${encodeURIComponent(execution.flow_id)}?run=${encodeURIComponent(execution.id)}`
    : null;
  const orphanDocument = !execution.flow_id && execution.status === 'completed'
    && execution.view_payload?.view_type === 'artifact_document'
    ? execution.view_payload : null;
  const originalDocumentTitle = orphanDocument?.data && 'title' in orphanDocument.data
    && typeof orphanDocument.data.title === 'string' ? orphanDocument.data.title : null;
  const launchInputs = extractLaunchInputs(execution.input_values, execution.flow_snapshot);
  const hasFailedStep = steps.some((step) => step.status === 'failed');
  const runLevelErrorMessage =
    execution.status === 'failed' && !hasFailedStep
      ? extractExecutionErrorMessage(execution.error)
      : null;

  return (
    <div className="w-full p-6 xl:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">
            {orphanDocument ? documentTitle ?? originalDocumentTitle ?? snapshot?.title ?? 'Résultat' : snapshot?.title || 'Exécution'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {new Date(execution.created_at).toLocaleString('fr-FR')}
          </p>
        </div>
        {!orphanDocument && <ExecutionStatusBadge status={execution.status} />}
      </div>

      {runLevelErrorMessage && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="font-medium">Échec détecté hors étape visible</p>
          <p className="mt-1 break-words text-red-700">{runLevelErrorMessage}</p>
        </div>
      )}

      {!orphanDocument && launchInputs.length > 0 && (
        <div className="mt-6">
          <ExecutionLaunchInputs inputs={launchInputs} />
        </div>
      )}

      {orphanDocument && (
        <section className="mt-6" aria-label="Document retrouvé">
          <WorkActionsProvider actions={[]} historical resultSource={{ kind: 'flow', id: execution.id }} onResultTitleChange={setDocumentTitle}>
            <ViewRouter payload={orphanDocument} hideTitle />
          </WorkActionsProvider>
        </section>
      )}

      <div className="mt-6 flex flex-col gap-4 xl:grid xl:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] xl:items-start">
        <div className="min-h-0 xl:sticky xl:top-8 xl:h-[calc(100vh-5rem)]">
          <div className="mb-3 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 xl:hidden">
            {toolResultHref ? (
              <Link
                to={toolResultHref}
                className="inline-flex h-8 items-center justify-center rounded-lg border border-domino-cardBorder bg-domino-white px-3 text-xs font-medium text-domino-ink shadow-sm transition hover:border-domino-aubergine hover:bg-domino-muted"
              >
                Voir le résultat dans le tool
                <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Link>
            ) : (
              <span />
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsTimelineDrawerOpen(true)}
            >
              Voir les étapes
            </Button>
          </div>
          <ExecutionChatPanel
            executionId={execution.id}
            executionStatus={execution.status}
            focusStepId={focusedStepTarget?.stepId ?? null}
            focusRequestId={focusedStepTarget?.requestId ?? 0}
            toolResultHref={toolResultHref}
            className="h-[calc(100vh-14rem)] min-h-[560px] xl:h-full"
          />
        </div>

        <div className="hidden space-y-3 xl:block">
          <ExecutionTimelineRail
            steps={steps}
            snapshotDominos={snapshot?.dominos}
            executionStatus={execution.status}
            selectedStepId={focusedStepTarget?.stepId ?? null}
            onStepSelect={handleTimelineStepSelect}
          />
        </div>
      </div>

      {isTimelineDrawerOpen && (
        <div className="fixed inset-0 z-50 xl:hidden">
          <button
            type="button"
            aria-label="Fermer le drawer étapes"
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsTimelineDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-[88vw] max-w-md overflow-y-auto bg-white p-4 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Étapes (lecture seule)
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsTimelineDrawerOpen(false)}
              >
                Fermer
              </Button>
            </div>
            <div className="space-y-3">
              <ExecutionTimelineRail
                steps={steps}
                snapshotDominos={snapshot?.dominos}
                executionStatus={execution.status}
                selectedStepId={focusedStepTarget?.stepId ?? null}
                onStepSelect={handleTimelineStepSelect}
              />
            </div>
          </div>
        </div>
      )}

      {execution.status === 'completed' && (
        <FeedbackWidget
          executionId={execution.id}
          existingRating={execution.rating}
          existingNote={execution.feedback_note}
          onSubmitted={(rating: number, note: string | null) => {
            setExecution((previous) =>
              previous ? { ...previous, rating, feedback_note: note } : previous
            );
          }}
        />
      )}
    </div>
  );
};
